import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {PopoverControllerService} from '../../../../shared/shared-services/overlay/popover-controller.service';
import {CellClickedEvent, ColDef, GridOptions, GridReadyEvent, RowNode} from 'ag-grid-community';
import {PriceCellEditorComponent} from '../price-cell-editor/price-cell-editor.component';
import {MultipleErrorPopoverComponent} from '../multiple-error-popover/multiple-error-popover.component';
import {
  keyToTitle,
  stockValCCR2Str,
  StockValChangeCheckConfig,
  StockValuesChangeCheckResult
} from '../../../../shared-utilities/models-old/datastructures';
import {ModalControllerService} from '../../../../shared/shared-services/overlay/modal-controller.service';

type KeysOfStockValChangeCheckConfig = keyof Partial<StockValChangeCheckConfig>;

@Component({
  selector: 'app-stock-val-change-flags',
  templateUrl: './stock-val-change-flags.component.html',
  styleUrls: ['./stock-val-change-flags.component.scss'],
  standalone: false
})
export class StockValChangeFlagsComponent implements OnInit, OnChanges {

  @Input() desc?: 'Stock item name';
  @Input() itemFlags: Record<string, StockValuesChangeCheckResult>;
  @Input() codes: string[];
  @Input() storeId: string;

  rowData: RowNode[] = [];
  columnDefs: ColDef[] = [];
  gridOptions: GridOptions = {};
  changes = false;
  defaultColDef: ColDef = {editable: false, sortable: true, filter: false};
  resolvedValues: Record<string, Partial<Record<KeysOfStockValChangeCheckConfig, Record<string | number, string | number | Date>>>> = {};

  constructor(
    private modalController: ModalControllerService,
    private popControl: PopoverControllerService
  ) {
  }

  ngOnInit(): void {
    this.initializeGridData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['codes'] && !changes['itemFlags']) {
      return;
    }
    this.initializeGridData();
  }

  initializeGridData(): void {
    if (!this.codes?.length || !this.itemFlags) {
      return;
    }
    const allKeys = new Set<string>();
    this.codes.forEach(code => {
      Object.keys(this.itemFlags[code]).forEach(key => allKeys.add(key));
    });

    this.columnDefs = [
      {
        headerName: 'Code',
        field: 'code',
        width: 130
      },
      {
        headerName: 'Description',
        field: 'description',
        tooltipField: 'toolTip',
        onCellClicked: this.onShowErrorsClick
      },
      {
        headerName: 'Type',
        field: 'key2Title',
        width: 150
      },
      {
        headerName: 'New Value',
        field: 'new',
        editable: true,
        cellEditor: PriceCellEditorComponent,
        tooltipField: 'new'
      }
    ];

    this.getRowData();
    this.gridOptions = {singleClickEdit: true, tooltipShowDelay: 500};
  }

  getRowData(): void {
    this.rowData = this.codes.reduce((acc, code: string) => {
      const flaggedKeys = Object.keys(this.itemFlags[code]);

      const rows = flaggedKeys.reduce((innerAcc, key: string) => {
        const items = this.itemFlags[code][key];

        const descriptions = [];
        let formattedNew: string | number | Date;
        let original: string | number | Date;
        let type: string | number | Date;
        let key2Title: string;
        for (const item of items) {
          type = item.type;
          original = item.old;
          key2Title = keyToTitle[key];
          descriptions.push(stockValCCR2Str(item));
          if (!this.resolvedValues[code]) {
            this.resolvedValues[code] = {};
          }
          if (!this.resolvedValues[code][key]) {
            this.resolvedValues[code][key] = {};
          }
          this.resolvedValues[code][key][type] = item.new;
          if (key === 'sellPriIncl1') {
            formattedNew = item.new;
          } else {
            formattedNew = item.new;
          }
        }
        let isDescList;
        if (descriptions.length > 1) {
          isDescList = 'Click to see all Errors';
        } else {
          isDescList = descriptions[0];
        }
        innerAcc.push({
          code,
          description: descriptions[0],
          descriptionList: descriptions,
          toolTip: isDescList,
          new: formattedNew,
          original,
          type,
          key2Title,
          key
        });
        return innerAcc;
      }, []);
      return acc.concat(rows);
    }, []);
  }

  async onCancelClick(): Promise<void> {
    await this.modalController.dismiss(null, 'cancel').then();
  }

  async onChangeButtonClick(): Promise<void> {
    for (const idx of Object.keys(this.rowData)) {
      this.resolvedValues[this.rowData[idx].code][this.rowData[idx].key] =
        this.rowData[idx].new;
    }
    await this.modalController.dismiss(this.resolvedValues, 'submit').then();
  }

  onGridReady(params: GridReadyEvent): void {
    requestAnimationFrame(() => {
      params.api.sizeColumnsToFit();
    });
  }

  onCellValueChanged(): void {
    this.changes = true;
  }

  onShowErrorsClick = async (params: CellClickedEvent): Promise<void> => {
    if (params.data.descriptionList.length > 1) {
      const pc = await this.popControl.create({
        component: MultipleErrorPopoverComponent,
        backdropDismiss: true,
        componentProps: {
          description: params.data.descriptionList
        }
      });
      await pc.present();
    }
  };
}
